<template>

    <div>
        <div v-if="loading" class="text-center my-2">
            <b-spinner class="align-middle" />
            <div>
                <strong>Loading...</strong>
            </div>

        </div>
        <div v-else-if="loadError && !accessDenied" class="text-center p-4">
            <strong>There was an error loading PAR Level Items.</strong>
        </div>
        <div v-else-if="accessDenied" class="text-center p-4">
            <div class="alert alert-info">
                <i class="fa fa-info-circle"></i>
                <strong>PAR Level Information:</strong>
                <p>You don't have permission to view inventory PAR levels for this item.</p>
            </div>
        </div>
        <b-table v-if="!loading && !loadError && !accessDenied" :items="filteredItems" :fields="fields" :sort-by.sync="sortBy" :sort-desc.sync="sortDesc"
            :tbody-tr-class="rowClass" show-empty empty-text="No PAR Level entries to display.">

            <template #head(buyRxSelect)>
                <b-form-checkbox :checked="allSelected" :indeterminate="someSelected" @change="toggleSelectAll" />
            </template>

            <template #cell(buyRxSelect)="{ item }">
                <b-form-checkbox v-if="item.ndc" :checked="isSelected(item)" @change="toggleSelect(item)" />
            </template>

            <template #cell(inAlert)="{ value }">
                <i v-if="value" class="fa fa-fw fa-warning" />
            </template>

            <template #cell(includeEquivalents)="{ item }">
                <i v-if="item.includeEquivalents" class="fa fa-fw fa-check text-success" />
                <span v-if="item.includeEquivalents && item.restrictEquivalentsToSameBrandGeneric"
                      class="text-muted ml-1" title="Same brand/generic only">
                    Same B/G
                </span>
            </template>

            <template #cell(actions)="data">
                <div>
                    <b-button variant="primary" title="Edit" size="sm" @click="edit(data.item)">
                        <b-icon icon="pencil" />
                    </b-button>
                    <b-button variant="danger" title="Delete" size="sm" @click="deleteItem(data.item)">
                        <b-icon icon="trash" />
                    </b-button>
                </div>
            </template>

            <template #cell(unitsOnHand)="data">
                {{ data.item.unitsOnHand }}
                <div v-if="data.item.unitsOnHand">
                    <b-badge :variant="data.item.inAlert ? 'danger' : 'primary'">
                        Current: {{ data.item.currentInventoryQuantity }}
                    </b-badge>
                </div>
            </template>

            <template #cell(daysOnHand)="data">
                {{ data.item.daysOnHand }}
                <div v-if="data.item.daysOnHand">
                    <b-badge :variant="data.item.inAlert ? 'danger' : 'primary'">
                        Current: {{ data.item.currentDaysOnHand }}
                    </b-badge>
                </div>
            </template>

        </b-table>

        <div v-if="buyRxVisible && selectedRows.length" class="mb-2">
            <b-button variant="primary" :disabled="addingToCart" @click="addSelectedToCart">
                <b-spinner v-if="addingToCart" small />
                Add {{ selectedRows.length }} to BuyRx Cart
            </b-button>
            <small class="text-muted ml-2">
                Reorder quantity is the number of packages needed to reach the PAR target; rows already at
                or above target are skipped.
            </small>
        </div>

        <div v-if="!accessDenied" class="text-center">
            <b-button variant="success" @click="add">
                Add PAR Level
            </b-button>
        </div>

        <b-modal id="par-level-modal" title="Inventory PAR Level" no-fade centered @ok="saveItem">
            <template v-if="selectedItem">
                <b-form-group v-if="selectedItem.id" label="Drug:">
                    <b-input :value="selectedItem.drugName" readonly />
                </b-form-group>
                <SearchComponent v-else :id="selectedDrug?.drugId" v-model="selectedDrug" :label="`Drug`"
                    :search-u-r-l="`/Drug`" required :disabled="drug"
                    :params="{ onlyActive: false, allSources: true }"
                    :params-in-get-by-id="{ drugSource: selectedDrug?.source }" />

                <SearchComponent v-model="selectedStore" label="Store" search-u-r-l="/store" prepopulate required />
                <SearchComponent v-model="selectedProgram" label="Program" search-u-r-l="/Program" prepopulate
                    clearable :results-modifier="addProgramOptions" />
                <SearchComponent v-if="hasInventoryLocations" :id="selectedLocation?.id" v-model="selectedLocation"
                    search-u-r-l="/inventory/inventory-locations" prepopulate label="Location"
                    :results-modifier="addLocationOptions" />
                <b-row>
                    <b-col>
                        <b-form-group label="Units on Hand">
                            <b-form-input v-model="selectedItem.unitsOnHand" type="number" />
                        </b-form-group>
                    </b-col>
                    <b-col>
                        <b-form-group label="Days on Hand">
                            <b-form-input v-model="selectedItem.daysOnHand" type="number" />
                        </b-form-group>
                    </b-col>
                </b-row>
                <b-form-checkbox v-model="selectedItem.includeEquivalents">
                    Include Equivalents
                    <HelpPopup text="When enabled, therapeutically equivalent drugs are included when calculating current inventory levels for PAR alerts." />
                </b-form-checkbox>
                <b-form-checkbox
                    v-model="selectedItem.restrictEquivalentsToSameBrandGeneric"
                    :disabled="!selectedItem.includeEquivalents"
                    class="ml-4"
                >
                    Same Brand/Generic Only
                    <HelpPopup text="When enabled, the equivalent-on-hand rollup only counts equivalents whose Brand/Generic status matches the par drug. Keeps a generic par from masking brand stock (and vice versa). Has no effect when Include Equivalents is off." />
                </b-form-checkbox>
            </template>
        </b-modal>
    </div>
</template>

<script lang="ts">
import { InventoryParLevelViewModel } from "@/models/InventoryParLevel";
import { Component, Vue, Prop } from "vue-property-decorator";
import SearchComponent from '@/components/SearchComponent.vue';
import HelpPopup from '@/components/HelpPopup.vue';
import { Drug } from "@/models/Drug/Drug";
import { Store } from "@/models/Store";
import { Program } from "@/models/Program";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import TextUtils from "@/util/TextUtils";
import { toRaw } from "vue";
import { InventoryLocation } from "@/models/InventoryLocation";

const anyLocation = Object.assign(new InventoryLocation(), { id: null, name: "Any Location" });
const allPrograms = Object.assign(new Program(), { id: null, name: "All Programs" });

@Component({
    name: "PeriodicInventoryDetailsPage",
    components: {
        SearchComponent,
        HelpPopup
    }
})
export default class InventoryParLevelsComponent extends Vue {

    @Prop() protected drug?: Drug;

    private items: InventoryParLevelViewModel[] = [];
    private sortBy = "inAlert";
    private sortDesc = true;
    private selectedItem: InventoryParLevelViewModel | null = null;
    private selectedDrug: Drug = new Drug();
    private selectedStore: Store = new Store();
    private selectedProgram: Program = new Program();
    private selectedLocation: InventoryLocation = new InventoryLocation();
    private loading = true;
    private loadError = false;
    private accessDenied = false;

    // CheckMyRxCost (BuyRx.ai) bulk add-to-cart
    private buyRxConfigured = false;
    private buyRxHasPermission = false;
    // Keyed by PAR level id, not NDC: the same NDC legitimately appears on several rows (one per
    // store/program/location), and keying by NDC made ticking one row tick all of its siblings.
    private selectedRowIds: number[] = [];
    private addingToCart = false;

    mounted() {
        this.loadHasInventoryLocations();
        this.loadItems();
        this.loadBuyRxStatus();
    }

    get fields() {
        return [
            { key: "buyRxSelect", label: "", class: "buy-rx-select-col", hidden: !this.buyRxVisible },
            { key: "inAlert", sortable: true, class: "in-alert-col", label: "Alert" },
            { key: "drugName" },
            { key: "ndc", label: "NDC", formatter: Drug.formatNDC542 },
            { key: "storeName" },
            { key: "programName" },
            { key: "locationName", hidden: !this.hasInventoryLocations },
            { key: "unitsOnHand" },
            { key: "daysOnHand" },
            { key: "includeEquivalents", label: "Equivalents" },
            { key: "actions", label: "", class: "actions-col" }
        ].filter(f => f.hidden !== true)
    }

    rowClass(item: InventoryParLevelViewModel, type: string) {
        if (!item || type !== 'row') return
        if (item.inAlert) return 'table-danger in-alert-row'
    }

    private add() {
        this.selectedItem = {
            id: 0,
            includeEquivalents: false,
            restrictEquivalentsToSameBrandGeneric: false,
            created: new Date(),
            createdBy: "",
            updated: new Date(),
            updatedBy: ""
        };
        this.selectedDrug = this.drug ?? new Drug();
        this.selectedStore = new Store();
        this.selectedProgram = new Program();
        this.selectedLocation = anyLocation;

        this.$bvModal.show("par-level-modal");
    }

    private edit(item: InventoryParLevelViewModel) {
        this.selectedItem = structuredClone(toRaw(item));
        this.selectedDrug = Object.assign(new Drug(item.drugId), { source: item.drugSource });
        this.selectedStore = Object.assign(new Store(), { id: item.storeId });
        this.selectedProgram = item.programId === null
            ? allPrograms
            : Object.assign(new Program(), { id: item.programId, name: item.programName ?? "" });
        this.selectedLocation = Object.assign(new InventoryLocation(), { id: item.locationId, name: item.locationName });
        this.$bvModal.show("par-level-modal");
    }

    async loadItems() {
        try {
            this.loadError = false;
            this.accessDenied = false;
            this.loading = true;
            const response = await this.$http.get<InventoryParLevelViewModel[]>("/inventory/par-levels");

            this.items = response.data;
        } catch (err: any) {
            if (err.response?.status === 403) {
                this.accessDenied = true;
            } else {
                this.loadError = true;
                this.$notification(NotificationOptions.error(err));
            }
        } finally {
            this.loading = false;
        }
    }

    get filteredItems() {
        let items = this.items;
        if (this.drug) {
            items = items.filter(i => i.drugSource === this.drug?.source && i.drugId === this.drug?.drugId);
        }
        return items;
    }

    async deleteItem(item: InventoryParLevelViewModel) {
        try {
            const confirm = await this.$bvModal.msgBoxConfirm("Are you sure you want to delete this Inventory PAR Level?",
                {
                    title: "Delete Inventory PAR Level",
                    noFade: true,
                    centered: true,
                    okTitle: "Delete",
                    okVariant: "danger"
                });

            if (!confirm) return;

            await this.$http.delete("/inventory/par-level/" + item.id);
            this.loadItems();
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    async saveItem(e?: any) {
        e?.preventDefault();

        try {

            const errorMsgOptions = { title: "Error", centered: true, noFade: true };

            if (!this.selectedDrug?.drugId) {
                this.$bvModal.msgBoxOk("You must select a drug.", errorMsgOptions);
                return;
            }

            if (!this.selectedStore?.id) {
                this.$bvModal.msgBoxOk("You must select a store.", errorMsgOptions);
                return;
            }

            if (!this.selectedItem!.unitsOnHand && !this.selectedItem!.daysOnHand) {
                this.$bvModal.msgBoxOk("You must enter a value for Units on Hand or Days on Hand.", errorMsgOptions);
                return;
            }

            if (!this.selectedItem!.id) {
                this.selectedItem!.drugSource = this.drug?.source ?? this.selectedDrug.source;
                this.selectedItem!.drugId = this.drug?.id ?? this.selectedDrug.id;
            }

            this.selectedItem!.storeId = this.selectedStore.id as number;
            // Distinguish "All Programs" (explicit null) from the empty default (treated as
            // "no program", which persists as 0 to preserve existing behavior).
            this.selectedItem!.programId = this.selectedProgram?.id === undefined ? 0 : this.selectedProgram.id;
            this.selectedItem!.unitsOnHand = !this.selectedItem!.unitsOnHand ? null : this.selectedItem!.unitsOnHand;
            this.selectedItem!.daysOnHand = !this.selectedItem!.daysOnHand ? null : this.selectedItem!.daysOnHand;
            this.selectedItem!.locationId = this.selectedLocation.id;

            await this.$http.post("/inventory/par-level", this.selectedItem);

            this.loadItems();

            this.$bvModal.hide("par-level-modal");
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    addLocationOptions(locations: Array<InventoryLocation>, params: { searchTerm: string }) {
        if (params.searchTerm?.trim()) return locations;
        return [
            anyLocation,
            Object.assign(new InventoryLocation(), { id: 0, name: "No Location Specified" }),
            { heading: true, label: "Locations" },
            ...locations
        ];
    }

    addProgramOptions(programs: Array<Program>, params: { searchTerm: string }) {
        if (params.searchTerm?.trim()) return programs;
        return [
            allPrograms,
            { heading: true, label: "Programs" },
            ...programs
        ];
    }

    // -----------------------------------------------------------------------
    // CheckMyRxCost (BuyRx.ai) bulk add-to-cart
    // -----------------------------------------------------------------------

    get buyRxVisible(): boolean {
        return this.buyRxConfigured && this.buyRxHasPermission;
    }

    /** PAR items that can be added to the cart (those with an NDC). */
    get selectableItems(): InventoryParLevelViewModel[] {
        return this.filteredItems.filter(i => !!i.ndc);
    }

    /** Currently-visible rows the user has ticked. */
    get selectedRows(): InventoryParLevelViewModel[] {
        return this.selectableItems.filter(i => this.selectedRowIds.includes(i.id));
    }

    get allSelected(): boolean {
        return this.selectableItems.length > 0 && this.selectedRows.length === this.selectableItems.length;
    }

    get someSelected(): boolean {
        return this.selectedRows.length > 0 && !this.allSelected;
    }

    isSelected(item: InventoryParLevelViewModel): boolean {
        return this.selectedRowIds.includes(item.id);
    }

    toggleSelect(item: InventoryParLevelViewModel) {
        const idx = this.selectedRowIds.indexOf(item.id);
        if (idx >= 0) this.selectedRowIds.splice(idx, 1);
        else this.selectedRowIds.push(item.id);
    }

    toggleSelectAll(checked: boolean) {
        this.selectedRowIds = checked ? this.selectableItems.map(i => i.id) : [];
    }

    /**
     * Number of packages to reorder. The PAR target and current inventory are in units, but the BuyRx
     * cart quantity is a package count, so the unit shortfall is divided by the drug's package size.
     * Returns 0 when a target is already met/exceeded (nothing to reorder). Days-on-hand PAR levels have
     * no unit target to derive a package shortfall from, so they reorder a single package only when the
     * row is in alert (below its days-on-hand target), and are skipped otherwise.
     */
    private reorderQuantity(item: InventoryParLevelViewModel): number {
        const target = item.unitsOnHand ?? 0;
        if (target <= 0) return item.inAlert ? 1 : 0;

        const current = item.currentInventoryQuantity ?? 0;
        const unitShortfall = target - current;
        if (unitShortfall <= 0) return 0;

        const packageSize = item.packageSize && item.packageSize > 0 ? item.packageSize : 1;
        return Math.ceil(unitShortfall / packageSize);
    }

    async loadBuyRxStatus() {
        try {
            const { data } = await this.$http.get<{ configured: boolean; hasPermission: boolean }>(
                "/CheckMyRxCost/status");
            this.buyRxConfigured = data.configured;
            this.buyRxHasPermission = data.hasPermission;
        } catch {
            this.buyRxConfigured = false;
        }
    }

    async addSelectedToCart() {
        // The cart belongs to a single pharmacy, so selected rows that share an NDC (different stores,
        // programs or locations) have to combine into one line rather than several competing ones.
        const byNdc = new Map<string, number>();
        for (const item of this.selectedRows) {
            const quantity = this.reorderQuantity(item);
            // Skip rows already at/above their PAR target (no packages needed).
            if (quantity <= 0) continue;
            // The platform's API keys everything by the raw 11-digit NDC, without hyphens.
            const ndc = TextUtils.normalizeNdc11(item.ndc);
            byNdc.set(ndc, (byNdc.get(ndc) ?? 0) + quantity);
        }
        const items = [...byNdc].map(([NDC, Quantity]) => ({ NDC, Quantity }));

        if (!items.length) {
            this.$notification(NotificationOptions.warning(
                "The selected item(s) are already at or above their PAR target — nothing to add."));
            return;
        }

        try {
            this.addingToCart = true;
            const { data } = await this.$http.post<{ added: number }>("/CheckMyRxCost/cart/list", items);
            this.$notification(NotificationOptions.success(`Added ${data?.added ?? items.length} item(s) to the BuyRx cart.`));
            this.selectedRowIds = [];
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.addingToCart = false;
        }
    }

    protected hasInventoryLocations = false;

    async loadHasInventoryLocations() {
        try {
            this.hasInventoryLocations = (await this.$http.get<boolean>("/inventory/has-inventory-locations")).data;
        } catch {
            //ignore
        }
    }

}

</script>

<style scoped lang="scss">
::v-deep(.buy-rx-select-col) {
    width: 40px;
    text-align: center;
}

::v-deep(.in-alert-col) {
    width: 50px;
    text-align: center;
}

::v-deep(.in-alert-row) {
    font-weight: bold;
}

::v-deep(.actions-col) {
    width: 110px;

    >div {
        display: flex;
        gap: 5px;
    }

}
</style>
